import { formatCents, formatPhoneTyping, parseDollars } from "@vaulted/shared";
import { type ButtonHTMLAttributes, type InputHTMLAttributes, type ReactNode, type Ref, type SelectHTMLAttributes, type TextareaHTMLAttributes, useEffect, useId, useRef, useState } from "react";
import { NavLink } from "react-router";

const cx = (...c: (string | false | null | undefined)[]) => c.filter(Boolean).join(" ");

type Variant = "primary" | "secondary" | "quiet" | "danger";

export function Button({
  variant = "secondary",
  busy,
  className,
  children,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: Variant; busy?: boolean }) {
  const styles: Record<Variant, string> = {
    primary: "bg-brand text-white hover:bg-brand-deep disabled:bg-brand/50",
    secondary: "bg-white text-ink border border-line hover:border-ink-soft disabled:text-muted",
    quiet: "text-ink-soft hover:bg-mist disabled:text-muted",
    danger: "bg-white text-danger border border-danger/30 hover:bg-danger-wash",
  };
  return (
    <button
      className={cx(
        "inline-flex items-center justify-center gap-2 rounded-[var(--radius-control)] px-4 py-2 text-sm font-semibold transition-colors disabled:cursor-not-allowed",
        styles[variant],
        className,
      )}
      disabled={busy || rest.disabled}
      {...rest}
    >
      {busy && <span className="size-3.5 animate-spin rounded-full border-2 border-current border-r-transparent" aria-hidden />}
      {children}
    </button>
  );
}

const controlClass =
  "w-full rounded-[var(--radius-control)] border border-line bg-white px-3 py-2 text-[15px] text-ink placeholder:text-muted/70 hover:border-ink-soft/50 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20 disabled:bg-mist";

/**
 * For search boxes: no browser autofill, autocorrect, or spell check. They'd swap or fill in names
 * (on space, or when a result is clicked) and change what's being searched for.
 */
export const searchInputProps = { autoComplete: "off", autoCorrect: "off", spellCheck: false } as const;

export function Field({
  label,
  hint,
  error,
  className,
  labelClassName,
  inputClassName,
  ...rest
}: InputHTMLAttributes<HTMLInputElement> & { label: string; hint?: string; error?: string; ref?: Ref<HTMLInputElement>; /** Pages with their own look (event pages). */ labelClassName?: string; inputClassName?: string }) {
  const id = useId();
  return (
    <div className={className}>
      <label htmlFor={id} className={labelClassName ?? "mb-1 block text-sm font-semibold text-ink-soft"}>
        {label}
      </label>
      <input
        id={id}
        className={inputClassName ?? controlClass}
        aria-invalid={!!error}
        aria-describedby={hint ? `${id}-hint` : undefined}
        {...(rest.type === "search" ? searchInputProps : {})}
        {...rest}
        // Phone numbers format themselves as they're typed: (405) 210-7822.
        onChange={
          rest.type === "tel" && rest.onChange
            ? (e) => {
                e.target.value = formatPhoneTyping(e.target.value);
                rest.onChange!(e);
              }
            : rest.onChange
        }
      />
      {hint && !error && (
        <p id={`${id}-hint`} className="mt-1 text-sm text-muted">
          {hint}
        </p>
      )}
      {error && <p className="mt-1 text-sm text-danger">{error}</p>}
    </div>
  );
}

export function SelectField({
  label,
  hint,
  children,
  className,
  ...rest
}: SelectHTMLAttributes<HTMLSelectElement> & { label: string; hint?: string }) {
  const id = useId();
  return (
    <div className={className}>
      <label htmlFor={id} className="mb-1 block text-sm font-semibold text-ink-soft">
        {label}
      </label>
      <select id={id} className={controlClass} {...rest}>
        {children}
      </select>
      {hint && <p className="mt-1 text-sm text-muted">{hint}</p>}
    </div>
  );
}

export function Badge({ tone, children }: { tone: "ok" | "wait" | "off" | "brand"; children: ReactNode }) {
  const tones = {
    ok: "bg-ok-wash text-ok",
    wait: "bg-wait-wash text-wait",
    off: "bg-mist text-muted",
    brand: "bg-brand-wash text-brand-deep",
  };
  return <span className={cx("inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold", tones[tone])}>{children}</span>;
}

export function Panel({ children, className }: { children: ReactNode; className?: string }) {
  return <section className={cx("rounded-[var(--radius-panel)] border border-line bg-white", className)}>{children}</section>;
}

export function PanelHeader({ title, description, action }: { title: string; description?: string; action?: ReactNode }) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-3 border-b border-mist px-5 py-4">
      <div>
        <h2 className="text-base font-semibold">{title}</h2>
        {description && <p className="mt-0.5 max-w-prose text-sm text-muted">{description}</p>}
      </div>
      {action}
    </div>
  );
}

export function PageHeader({ title, description, action }: { title: string; description?: string; action?: ReactNode }) {
  return (
    <header className="mb-6 flex flex-wrap items-end justify-between gap-4">
      <div>
        <h1 className="page-title">{title}</h1>
        {description && <p className="mt-1.5 max-w-prose text-ink-soft">{description}</p>}
      </div>
      {action}
    </header>
  );
}

export function Alert({ tone = "danger", children }: { tone?: "danger" | "ok" | "info"; children: ReactNode }) {
  const tones = { danger: "bg-danger-wash text-danger", ok: "bg-ok-wash text-ok", info: "bg-mist text-ink-soft" };
  return (
    <div role={tone === "danger" ? "alert" : "status"} className={cx("rounded-[var(--radius-control)] px-3.5 py-2.5 text-sm", tones[tone])}>
      {children}
    </div>
  );
}

export function Dialog({
  title,
  onClose,
  children,
  footer,
  wide,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  wide?: boolean;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const d = ref.current;
    if (d && !d.open) d.showModal();
  }, []);
  return (
    <dialog
      ref={ref}
      onClose={onClose}
      onCancel={(e) => {
        e.preventDefault();
        onClose();
      }}
      className={cx(
        "m-auto w-[calc(100%-2rem)] rounded-[var(--radius-panel)] border-0 bg-white p-0 text-ink shadow-2xl backdrop:bg-ink/40",
        wide ? "max-w-2xl" : "max-w-lg",
      )}
    >
      <div className="flex items-center justify-between border-b border-mist px-5 py-4">
        <h2 className="text-lg font-semibold">{title}</h2>
        <button onClick={onClose} className="rounded-md p-1 text-muted hover:bg-mist hover:text-ink" aria-label="Close">
          <svg viewBox="0 0 20 20" className="size-5" fill="none" stroke="currentColor" strokeWidth="1.8">
            <path d="M5 5l10 10M15 5L5 15" />
          </svg>
        </button>
      </div>
      <div className="px-5 py-5">{children}</div>
      {footer && <div className="flex justify-end gap-2 border-t border-mist px-5 py-4">{footer}</div>}
    </dialog>
  );
}

export function Loading() {
  return (
    <div className="flex items-center gap-3 py-10 text-muted" role="status">
      <span className="size-4 animate-spin rounded-full border-2 border-brand border-r-transparent" aria-hidden />
      Loading…
    </div>
  );
}

export function Checkbox({
  label,
  description,
  checked,
  onChange,
  disabled,
}: {
  label: string;
  description?: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
}) {
  const id = useId();
  return (
    <label htmlFor={id} className={cx("flex gap-3 rounded-[var(--radius-control)] px-2 py-2", disabled ? "opacity-60" : "cursor-pointer hover:bg-mist")}>
      <input
        id={id}
        type="checkbox"
        className="mt-1 size-4 accent-[var(--color-brand)]"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span>
        <span className="block font-medium">{label}</span>
        {description && <span className="block text-sm text-muted">{description}</span>}
      </span>
    </label>
  );
}

export function formatDateTime(iso: string | null, tz?: string) {
  if (!iso) return "—";
  return new Intl.DateTimeFormat("en-US", { dateStyle: "medium", timeStyle: "short", timeZone: tz }).format(new Date(iso));
}

/** Medical records (charts) use 24-hour time: "Oct 25, 2026, 15:02". */
export function formatDateTime24(iso: string | null, tz?: string) {
  if (!iso) return "—";
  return new Intl.DateTimeFormat("en-US", { dateStyle: "medium", timeStyle: "short", hourCycle: "h23", timeZone: tz }).format(new Date(iso));
}

export function TextAreaField({ label, hint, className, ...rest }: TextareaHTMLAttributes<HTMLTextAreaElement> & { label: string; hint?: string }) {
  const id = useId();
  return (
    <div className={className}>
      <label htmlFor={id} className="mb-1 block text-sm font-semibold text-ink-soft">
        {label}
      </label>
      <textarea id={id} rows={3} className={controlClass} {...rest} />
      {hint && <p className="mt-1 text-sm text-muted">{hint}</p>}
    </div>
  );
}

/**
 * Dollar amount input that works in cents. With `optional`, an empty box means null
 * (for example "same as the clinic price").
 */
export function MoneyField({
  label,
  hint,
  cents,
  onChange,
  optional,
  placeholder,
  className,
}: {
  label: string;
  hint?: string;
  cents: number | null;
  onChange: (cents: number | null) => void;
  optional?: boolean;
  placeholder?: string;
  className?: string;
}) {
  const id = useId();
  const show = (c: number | null) => (c === null ? "" : (c / 100).toFixed(2));
  const [text, setText] = useState(show(cents));
  const [bad, setBad] = useState(false);
  useEffect(() => {
    if (parseDollars(text) !== cents) setText(show(cents));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cents]);
  return (
    <div className={className}>
      <label htmlFor={id} className="mb-1 block text-sm font-semibold text-ink-soft">
        {label}
      </label>
      <div className="relative">
        <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-muted">$</span>
        <input
          id={id}
          inputMode="decimal"
          className={cx(controlClass, "pl-7")}
          value={text}
          placeholder={placeholder}
          aria-invalid={bad}
          required={!optional}
          onChange={(e) => {
            const v = e.target.value;
            setText(v);
            if (v.trim() === "" && optional) {
              setBad(false);
              onChange(null);
              return;
            }
            const c = parseDollars(v);
            setBad(c === null);
            if (c !== null) onChange(c);
          }}
          onBlur={() => !bad && setText(show(cents))}
        />
      </div>
      {bad ? <p className="mt-1 text-sm text-danger">Enter an amount like 25 or 25.50</p> : hint && <p className="mt-1 text-sm text-muted">{hint}</p>}
    </div>
  );
}

export const MINUTE_STEPS = [0, 5, 10, 15, 20, 25, 30, 45, 60, 75, 90, 105, 120, 150, 180, 210, 240, 270, 300, 360, 420, 480];

export function formatMinutes(m: number) {
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  const r = m % 60;
  return r ? `${h} hr ${r} min` : `${h} hr`;
}

export function MinutesField({ label, hint, value, onChange, className }: { label: string; hint?: string; value: number; onChange: (m: number) => void; className?: string }) {
  const steps = MINUTE_STEPS.includes(value) ? MINUTE_STEPS : [...MINUTE_STEPS, value].sort((a, b) => a - b);
  return (
    <SelectField label={label} hint={hint} value={value} onChange={(e) => onChange(Number(e.target.value))} className={className}>
      {steps.map((m) => (
        <option key={m} value={m}>
          {m === 0 ? "None" : formatMinutes(m)}
        </option>
      ))}
    </SelectField>
  );
}

/** Tab links; they wrap onto more rows when they don't fit. */
export function Tabs({ items }: { items: { to: string; label: string; end?: boolean }[] }) {
  return (
    <nav className="mb-6 flex flex-wrap gap-1 border-b border-line pb-2" aria-label="Sections">
      {items.map((t) => (
        <NavLink
          key={t.to}
          to={t.to}
          end={t.end}
          className={({ isActive }) =>
            cx("rounded-lg px-3 py-1.5 text-sm font-semibold whitespace-nowrap", isActive ? "bg-brand-wash text-brand-deep" : "text-muted hover:bg-mist hover:text-ink")
          }
        >
          {t.label}
        </NavLink>
      ))}
    </nav>
  );
}

/** Tabs that switch a section on the same page (Tabs above is for links). They wrap rather than run off narrow screens. */
export function TabButtons<K extends string>({ items, value, onChange, label }: { items: readonly (readonly [K, string])[]; value: K; onChange: (k: K) => void; label: string }) {
  return (
    <nav className="mb-6 flex flex-wrap gap-1 border-b border-line pb-2" aria-label={label}>
      {items.map(([key, text]) => (
        <button
          key={key}
          type="button"
          onClick={() => onChange(key)}
          aria-current={value === key ? "page" : undefined}
          className={cx("rounded-lg px-3 py-1.5 text-sm font-semibold", value === key ? "bg-brand-wash text-brand-deep" : "text-muted hover:bg-mist hover:text-ink")}
        >
          {text}
        </button>
      ))}
    </nav>
  );
}

export const money = formatCents;

export { cx };

/** "08021981", "8/2/1981" or "1981-08-02" → "1981-08-02"; null when it isn't a full, real date. */
export function parseDob(text: string): string | null {
  const iso = /^(\d{4})-(\d{2})-(\d{2})$/.exec(text.trim());
  const d = text.replace(/\D/g, "");
  const [y, m, day] = iso ? [iso[1]!, iso[2]!, iso[3]!] : d.length === 8 ? [d.slice(4), d.slice(0, 2), d.slice(2, 4)] : [];
  if (!y || !m || !day) return null;
  const date = new Date(Date.UTC(+y, +m - 1, +day));
  if (date.getUTCFullYear() !== +y || date.getUTCMonth() !== +m - 1 || date.getUTCDate() !== +day) return null;
  return `${y}-${m}-${day}`;
}

/** Digits as typed → "08", "08/0", "08/02/19"; slashes appear only before a digit, so backspace just works. */
const dobTyping = (raw: string) => {
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw.trim())) return raw.trim().replace(/^(\d{4})-(\d{2})-(\d{2})$/, "$2/$3/$1");
  const d = raw.replace(/\D/g, "").slice(0, 8);
  return d.length <= 2 ? d : d.length <= 4 ? `${d.slice(0, 2)}/${d.slice(2)}` : `${d.slice(0, 2)}/${d.slice(2, 4)}/${d.slice(4)}`;
};
const dobShown = (iso: string) => (/^\d{4}-\d{2}-\d{2}$/.test(iso) ? `${iso.slice(5, 7)}/${iso.slice(8, 10)}/${iso.slice(0, 4)}` : "");

/**
 * Date of birth as MM/DD/YYYY, typed straight through (08021981): no date picker, no Tab
 * between parts, dashes and slashes ignored. The value is YYYY-MM-DD, or "" until it's a full date.
 */
export function DateOfBirthField({
  label = "Date of birth",
  value,
  onChange,
  required,
  disabled,
  error,
  hint,
  className,
  max,
}: {
  label?: string;
  value: string;
  onChange: (iso: string) => void;
  required?: boolean;
  disabled?: boolean;
  error?: string;
  hint?: string;
  className?: string;
  /** Latest allowed date (YYYY-MM-DD); today when not given. */
  max?: string;
}) {
  const [text, setText] = useState(dobShown(value));
  const latest = max ?? new Date().toLocaleDateString("sv-SE");
  useEffect(() => {
    // Changed from outside (e.g. a form reset): show it.
    if (parseDob(text) !== (value || null)) setText(dobShown(value));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);
  const iso = parseDob(text);
  const problem = text.replace(/\D/g, "").length === 8 && !iso ? "That isn't a real date." : iso && iso > latest ? "That date is in the future." : undefined;
  return (
    <Field
      label={label}
      className={className}
      inputMode="numeric"
      autoComplete="bday"
      placeholder="MM/DD/YYYY"
      maxLength={10}
      required={required}
      disabled={disabled}
      hint={hint ?? "Month, day, year: just type the numbers."}
      error={error ?? problem}
      value={text}
      ref={(el) => el?.setCustomValidity(text && !iso ? "Enter the full date, like 08/02/1981" : problem ?? "")}
      onChange={(e) => {
        const t = dobTyping(e.target.value);
        setText(t);
        const p = parseDob(t);
        onChange(p && p <= latest ? p : "");
      }}
    />
  );
}
